import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from "react";
import { useWallet as useAptosWallet } from "@aptos-labs/wallet-adapter-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { QUERY_KEY, STORAGE_KEY } from "@/shared/constants/keys";
import { getMeRequest, signinAptosRequest } from "@/api/auth";
import type { AptosSignInBody, AuthToken, Me } from "@/shared/types/auth";
import { getCookie, removeCookie, setCookie } from "typescript-cookie";
import { useRouter } from "@tanstack/react-router";
import axiosInstance from "@/config/axios";
import { Platform } from "@/shared/types/common";
import { isEd25519 } from "@/utils/web3";
import { KeylessPublicKey, KeylessSignature } from "@aptos-labs/ts-sdk";
import { RoutePath } from "@/shared/types/route";
import { jwtDecode } from "jwt-decode";
import toast from "react-hot-toast";

type AuthType = {
  openConnectModal: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  platform: Platform | null;
  address: string | null;
  user: Me | null;
  resetAuth: () => void;
  setAccessToken: (token: string) => void;
  handleLogin: (network: Platform, walletName?: string) => void;
  handleLogout: () => void;
  setOpenConnectModal: (open: boolean) => void;
};

const AuthContext = createContext<AuthType | null>(null);

export const useAuth = (): AuthType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthContext");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Hooks
  const router = useRouter();

  const {
    account: aptosAccount,
    connect: aptosConnect,
    connected: isAptosConnected,
    isLoading: isAptosLoading,
    signMessage: aptosSignMessage,
    disconnect: aptosLogout,
  } = useAptosWallet();

  // State
  const [openConnectModal, setOpenConnectModal] = useState<boolean>(false);
  const [userData, setUserData] = useState<Me | null>(null);
  const [platform, setPlatform] = useState<Platform | null>(
    (localStorage.getItem(STORAGE_KEY.PLATFORM) as Platform) || null
  );
  const [address, setAddress] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [accessToken, setAccessToken] = useState<string | null>(
    getCookie(STORAGE_KEY.ACCESS_TOKEN) || null
  );
  const [needToSignMessage, setNeedToSignMessage] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const checkInfomationEnable = useMemo(() => {
    if (platform === Platform.APTOS) {
      return isAptosConnected && !!accessToken && !isAptosLoading;
    }
    return false;
  }, [platform, accessToken, isAptosConnected, isAptosLoading]);

  const { isLoading: isMeLoading, error: meError } = useQuery({
    queryKey: [QUERY_KEY.ME],
    queryFn: async () => {
      const me = await getMeRequest();
      const addr = getAddress();
      setAddress(addr ?? me.address);
      setIsAuthenticated(true);
      setUserData(me);
      return me;
    },
    enabled: checkInfomationEnable,
    retry: false,
  });

  const signInAptosMutation = useMutation({
    mutationFn: async (payload: AptosSignInBody) => {
      const res = await signinAptosRequest(payload);
      return res;
    },
    onSuccess: (data) => {
      const decoded = jwtDecode<{ is_admin: boolean }>(data.access_token);
      if (decoded.is_admin) {
        handleAssignToken(data);
      } else {
        toast.error("You are not authorized to access this resource");
      }
    },
    onError: (error) => {
      console.error("Error APTOS during verification:", error);
      handleLogout();
    },
  });

  const resetAuth = () => {
    setAddress(null);
    setIsAuthenticated(false);
    setAccessToken(null);
    setUserData(null);
    setPlatform(null);
    setNeedToSignMessage(false);
    setIsLoading(false);
  };

  const getAddress = () => {
    if (platform === Platform.APTOS && aptosAccount) {
      return aptosAccount.address.toString();
    }
    return null;
  };

  const handleAssignToken = (data: AuthToken) => {
    axiosInstance.defaults.headers.common[
      "Authorization"
    ] = `Bearer ${data.access_token}`;
    setAccessToken(data.access_token);
    setCookie(STORAGE_KEY.ACCESS_TOKEN, data.access_token || "", {
      expires: new Date(new Date().getTime() + 24 * 60 * 60 * 1000),
    });
    setAccessToken(data.access_token);
    setCookie(
      STORAGE_KEY.EXPIRE_ACCESS_TOKEN,
      new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toISOString()
    );
    setOpenConnectModal(false);
    router.navigate({
      to: "/",
    });
  };

  const handleLogin = (network: Platform, walletName?: string) => {
    setPlatform(network);
    localStorage.setItem(STORAGE_KEY.PLATFORM, network);
    if (network === Platform.APTOS && walletName) {
      setIsLoading(true);
      setNeedToSignMessage(true);
      aptosConnect(walletName);
      // go to APTOS(Sign in) useEffect
    }
  };

  const handleLogout = () => {
    if (platform === Platform.APTOS) {
      aptosLogout();
    }
    resetAuth();
    localStorage.clear();
    sessionStorage.clear();
    removeCookie(STORAGE_KEY.ACCESS_TOKEN);
    router.navigate({
      to: RoutePath.Login as string,
    });
  };

  // APTOS(Sign in): sign message to sign in
  useEffect(() => {
    const getAptosSignature = async () => {
      if (!aptosAccount) return;
      try {
        const nonce = Math.random().toString(36).substring(2, 15);
        const message = `Sign up for our app: ${nonce}`;
        const res = await aptosSignMessage({
          message,
          nonce,
        });
        let payload: AptosSignInBody;

        const isWalletFromEd25519 = isEd25519(
          aptosAccount.publicKey.toString()
        );
        if (!isWalletFromEd25519) {
          const encodedKeylessPubkey = new KeylessPublicKey(
            (aptosAccount.publicKey as any).publicKey.iss,
            (aptosAccount.publicKey as any).publicKey.idCommitment
          );

          const encodedKeylessSignature = new KeylessSignature({
            ...(res.signature as any).signature,
          });

          payload = {
            encoded_signature: encodedKeylessSignature.toString(),
            encoded_pubkey: encodedKeylessPubkey.toString(),
            message: res.fullMessage,
            ref_by: undefined,
          };
        } else {
          payload = {
            encoded_signature: res.signature.toString(),
            encoded_pubkey: aptosAccount.publicKey.toString(),
            message: res.fullMessage,
            ref_by: undefined,
          };
        }

        await signInAptosMutation.mutateAsync(payload);
      } catch (err) {
        // showTransactionSnackbar({
        //   status: "error",
        //   title: "Session Expired",
        //   message: "You need to sign message",
        // });
      } finally {
        setIsLoading(false);
        setNeedToSignMessage(false);
      }
    };

    if (
      platform === Platform.APTOS &&
      isAptosConnected &&
      !accessToken &&
      needToSignMessage
    ) {
      getAptosSignature();
    }
  }, [
    platform,
    isAptosConnected,
    accessToken,
    setIsLoading,
    needToSignMessage,
    setNeedToSignMessage,
  ]);

  // Web3:
  useEffect(() => {
    // API: Logout if unable to fetch user information.
    if (meError) {
      handleLogout();
      return;
    }
    // EVM: Logout not required — handled by Privy.

    // APTOS: Automatic logout occurs if the wallet is connected but the user does not sign.
    if (!needToSignMessage && isAptosConnected && !accessToken) {
      handleLogout();
      return;
    }

    // APTOS: Logout if the wallet is not connected, but the access_token is still valid
    if (!isAptosConnected && !isAptosLoading && accessToken) {
      const walletName = localStorage.getItem(STORAGE_KEY.APTOS_WALLET_NAME);
      // console.log(
      //   "APTOS(Logout if the wallet is not connected, but the access_token is still valid):::walletName:",
      //   walletName
      // );
      if (walletName) {
        aptosConnect(walletName);
      }
      return;
    }
  }, [needToSignMessage, isAptosConnected, accessToken, handleLogout, meError]);

  // Auto logout when access_token is expired
  useEffect(() => {
    if (isAuthenticated && accessToken) {
      const checkTokens = () => {
        const expireAccessToken = getCookie(STORAGE_KEY.EXPIRE_ACCESS_TOKEN);
        const currentDate = new Date();
        const expireDateUTC = new Date(expireAccessToken || "");
        const expireDate = new Date(expireDateUTC.getTime());
        const isExpired = expireDate <= currentDate;
        if (isExpired) {
          handleLogout();
          // showTransactionSnackbar({
          //   status: "error",
          //   title: "Session Expired",
          //   message: "Your session has expired. Please log in again.",
          // });
        }
      };

      const intervalId = setInterval(checkTokens, 5 * 60 * 1000);
      return () => clearInterval(intervalId);
    }
  }, [handleLogout, isAuthenticated, accessToken]);

  const auth = {
    openConnectModal,
    isAuthenticated,
    isLoading: isMeLoading || isAptosLoading || isLoading,
    address,
    user: userData ?? null,
    platform,
    resetAuth,
    setAccessToken,
    handleLogin,
    handleLogout,
    setOpenConnectModal,
  };

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
